import { Link } from "wouter";
import { FileText, ArrowRight } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import { getToolById } from "@/lib/tools-data";
import type { TryFirstLink } from "@/lib/workflow/workflowPlans";

/**
 * "What you get before you pay": the real files a Workflow Product produces and a link to the single tool that does
 * one step of it. Honest by design: a list of outputs and a free-to-try step, not invented screenshots.
 */
export function WorkflowPreviewBlock({ deliverables, tryFirst, testId = "wf-preview" }: { deliverables: string[]; tryFirst: TryFirstLink[]; testId?: string }) {
  const { localizedHref } = useLocale();
  const links = tryFirst.filter((l) => !!getToolById(l.toolId));
  return (
    <section className="mt-5 rounded-xl border bg-card p-5 text-left" data-testid={testId} aria-labelledby={`${testId}-h`}>
      <h3 id={`${testId}-h`} className="font-semibold">What you get before you pay</h3>
      <p className="mt-1 text-sm text-muted-foreground">These are the files this product produces when you finish.</p>
      <ul className="mt-3 space-y-2 text-sm">
        {deliverables.map((t) => <li key={t} className="flex gap-2"><FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden /><span>{t}</span></li>)}
      </ul>
      {links.length > 0 && (
        <div className="mt-4 border-t pt-3" data-testid={`${testId}-try`}>
          <h4 className="text-sm font-semibold">Try one step first</h4>
          <p className="text-xs text-muted-foreground">A single tool does one step of this job, with trial uses before you pay for the whole product.</p>
          <ul className="mt-2 space-y-1 text-sm">
            {links.map((l) => (
              <li key={l.toolId}><Link href={localizedHref(`/tool/${l.toolId}`)} className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline">{l.label}<ArrowRight className="h-3.5 w-3.5" aria-hidden /></Link></li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
